import { OkamiHttpGatewayError, isForbiddenError, isUnauthorizedError } from '@/api/errors.ts'
import { refreshTokenCall } from '@/api/refresh-token.ts'
import { eventsManager } from '@/utils/events-manager.ts'
import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import { toast } from 'sonner'

export const okamiHttpGateway = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
})

// Bare instance for the refresh call only. It must NOT carry the response
// interceptor below: a 401 on the refresh itself would re-enter it, queue on its
// own result and deadlock the whole client.
export const okamiRefreshGateway = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
})

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean }

let isRefreshing = false

// Requests that got a 401 while a refresh was already in flight. They wait here
// and are replayed once the refresh settles, instead of failing.
let pendingRequests: {
  resolve: (value: unknown) => void
  reject: (error: unknown) => void
  config: RetriableConfig
}[] = []

function replayPendingRequests() {
  const queue = pendingRequests
  pendingRequests = []

  queue.forEach(({ resolve, reject, config }) => {
    config._retry = true
    okamiHttpGateway(config).then(resolve).catch(reject)
  })
}

function rejectPendingRequests(error: unknown) {
  const queue = pendingRequests
  pendingRequests = []

  queue.forEach(({ reject }) => reject(error))
}

// wrapped so the type predicate on axios.isCancel does not narrow the error
// parameter below to `never` in the non-cancel branch
const isCanceledRequest = (error: unknown): boolean => axios.isCancel(error)

okamiHttpGateway.interceptors.response.use(
  (response) => response,
  async (error: OkamiHttpGatewayError) => {
    // An aborted request is not a failure. Re-wrapping it would drop the
    // __CANCEL__ flag that axios.isCancel (and react-query) rely on, turning
    // every unmount and every cancelQueries into a visible error.
    if (isCanceledRequest(error)) {
      return Promise.reject(error)
    }

    const originalRequest = error.config as RetriableConfig | undefined

    if (isUnauthorizedError(error) && originalRequest && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject, config: originalRequest })
        })
      }

      isRefreshing = true

      try {
        await refreshTokenCall()

        isRefreshing = false

        replayPendingRequests()

        // _retry marks it as already-refreshed: if it 401s again the session is
        // dead, and we stop instead of refreshing forever.
        originalRequest._retry = true

        return okamiHttpGateway(originalRequest)
      } catch (e) {
        isRefreshing = false
        eventsManager.dispatch('auth:unauthenticated', { time: Date.now() })

        rejectPendingRequests(e)

        return Promise.reject(e)
      }
    }

    // Request já marcado como _retry voltou com 401: o refresh não salvou a
    // sessão. Despacha o evento para que o AppLayout redirecione para o login.
    if (isUnauthorizedError(error) && originalRequest?._retry) {
      eventsManager.dispatch('auth:unauthenticated', { time: Date.now() })
    }

    if (isForbiddenError(error)) {
      toast.error('Você não tem as permissões necessárias para acessar')
    }

    return Promise.reject(
      new OkamiHttpGatewayError(error.message, error.code, error.config, error.request, error.response),
    )
  },
)

export const customInstance = <T>(config: AxiosRequestConfig, options?: AxiosRequestConfig): Promise<T> => {
  return okamiHttpGateway({
    ...config,
    ...options,
  }).then(({ data }) => data)
}
